import React, { useState, useEffect } from "react";
import { Button, ButtonGroup } from "reactstrap";
import UserRoles from "../../constants/UserRoles";
import Framework from "@beardeddevops/react.framework";
import Types from "../../constants/Types";
import API from "@beardeddevops/react.api";
import FormControl from "@material-ui/core/FormControl/FormControl";
import InputLabel from "@material-ui/core/InputLabel/InputLabel";
import Select from "react-select";
import IconButton from "@material-ui/core/IconButton";
import moment from "moment";
import LeftArrowIcon from "@material-ui/icons/ChevronLeft";
import RightArrowIcon from "@material-ui/icons/ChevronRight";

export default function ScheduleHeader({ parent, roles, type }) {
	const [availableManagers, setAvailableManagers] = useState([]);
	const [overridedManagers, setOverridedManagers] = useState([]);

	const filterWeek = parent.state.filterWeek; // Always Sunday

	useEffect(() => {
		fetchManagers();
	}, [roles, type, parent]);

	async function fetchManagers(callParent = false) {
		if (!roles.includes(UserRoles.Admin)) return;
		const data = await updateData();
		setAvailableManagers(data?.available ?? []);
		setOverridedManagers(data?.overrided ?? []);

		if (callParent) {
			await parent.refreshPage();
		}
	}

	async function updateData() {
		let userModel = new API.BuildClass(
			Types.Managers._name,
			API.RequestType.READ_ALL
		);
		userModel.limit = 50;
		userModel.filter = {type};
		return await userModel.submit(null, "GET_SUBROLE");
	}

	/* ==========================
	   WEEK NAVIGATION
	========================== */

	const goToPrevWeek = async () => {
		const newDate = moment(filterWeek).subtract(7, "days").day(0).format("YYYY-MM-DD");
		parent.setState({filterWeek: newDate}, async () => {
			await parent.refreshPage();
		});
	};

	const goToNextWeek = async () => {
		const newDate = moment(filterWeek).add(7, "days").day(0).format("YYYY-MM-DD");
		parent.setState({filterWeek: newDate}, async () => {
			await parent.refreshPage();
		});
	};

	const goToThisWeek = async () => {
		const newDate = moment().day(0).format("YYYY-MM-DD");
		parent.setState({filterWeek: newDate}, async () => {
			await parent.refreshPage();
		});
	};

	/* ==========================
	   WEEK LABEL (Sun → Sat)
	========================== */

	const labelStart = moment(filterWeek);
	const labelEnd = moment(filterWeek).add(6, "days");

	return (
		<div style={{width: "100%"}}>

			{/* ===================== ROW 1 ===================== */}
			<div
				className="rbc-toolbar pl-3 pr-3"
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: "0px",
					width: "100%"
				}}
			>

				{/* ===== LEFT: WEEK ARROWS + THIS WEEK ===== */}
				<div className="flex gap-1" style={{display: "flex", alignItems: "center"}}>
					<IconButton onClick={goToPrevWeek} disabled={filterWeek === moment().day(0).format("YYYY-MM-DD")}>
						<LeftArrowIcon/>
					</IconButton>

					<button className="rbc-today" style={{width: "auto"}} type="button" onClick={goToThisWeek}>
						This Week
					</button>

					<IconButton onClick={goToNextWeek}>
						<RightArrowIcon/>
					</IconButton>

					<div className={"rbc-toolbar-label text-center sm:text-left"}>
						<label>
							{moment(filterWeek).format('ddd MMM D')} - {moment(filterWeek).add(6, 'days').format('ddd MMM D')}
						</label>
					</div>
				</div>

				{/* ===== CENTER: DAYS (DAY VIEW) OR OVERRIDES (WEEK VIEW) ===== */}
				<div style={{flexGrow: 1, display: "flex", justifyContent: "center"}}>
					{parent.state.currentView === "day" ? (
						/* ----- Day Buttons in center ----- */
						<ButtonGroup size="md">
							{["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((day) => (
								<Button
									key={day}
									onClick={() => parent.setState({currentDay: day})}
									outline={parent.state.currentDay !== day}
									active={parent.state.currentDay === day}
									color="primary"
								>
									{day}
								</Button>
							))}
						</ButtonGroup>
					) : (
						/* ----- Overrides appear in center if WEEK VIEW ----- */
						roles.includes(UserRoles.Admin) && (
							<div style={{minWidth: "28rem", maxWidth: "32rem"}}>
								<FormControl fullWidth variant="outlined" size="large" style={{position: "relative"}}>
									{overridedManagers.length !== 0 && (
										<InputLabel style={{paddingLeft: 5, paddingRight: 5, zIndex: 1, top: "-5px"}} shrink={overridedManagers.length > 0}>Department Manager Overrides</InputLabel>
									)}

									<Select
										placeholder={"Department Manager Overrides"}
										isMulti
										isClearable={false}
										value={overridedManagers.map((item) => ({
											label: item.user_name,
											value: item.user,
											pkey: item.Pkey,
										}))}
										onChange={async (newValues, actionMeta) => {
											if (actionMeta.action === "select-option") {
												const added = actionMeta.option;
												let userModel = new API.BuildClass(
													Types.Department_Overrides._name,
													API.RequestType.CREATE
												);

												userModel.object.user = added.value;
												userModel.object.role_name = type;

												await userModel.submit(null, "PUT");
											}

											if (actionMeta.action === "remove-value") {
												const removed = actionMeta.removedValue;

												let userModel = new API.BuildClass(
													Types.Department_Overrides._name,
													API.RequestType.DELETE
												);

												userModel.object.user = removed.value;
												userModel.object.pkey = removed.pkey;
												userModel.object.role_name = type;

												await userModel.submit(null, "DELETE");
											}

											await fetchManagers(true);
										}}
										options={availableManagers.map((item) => ({
											label: item.user_name,
											value: item.pkey,
										}))}
										styles={{
											control: (base, state) => ({
												...base,
												minHeight: 40,
												borderColor: state.isFocused ? "#1976d2" : "#ccc",
												boxShadow: state.isFocused ? "0 0 0 1px #1976d2" : "none",
												"&:hover": {
													borderColor: state.isFocused ? "#1976d2" : "#888",
												},
											}),
											menuPortal: (base) => ({ ...base, zIndex: 9999 }),
										}}
										menuPortalTarget={document.body}
									/>
								</FormControl>
							</div>
						)
					)}
				</div>

				{/* ===== RIGHT: VIEW TOGGLE ===== */}
				<div>
					<ButtonGroup size="md">
						<Button
							onClick={() => parent.setState({currentView: "day"})}
							outline={parent.state.currentView !== "day"}
							active={parent.state.currentView === "day"}
							color="primary"
						>
							Day View
						</Button>
						<Button
							onClick={() => parent.setState({currentView: "week"})}
							outline={parent.state.currentView !== "week"}
							active={parent.state.currentView === "week"}
							color="primary"
						>
							Week View
						</Button>
					</ButtonGroup>
				</div>

			</div>

			{/* ===================== ROW 2 (Day View Only) ===================== */}
			{parent.state.currentView === "day" && roles.includes(UserRoles.Admin) && (
				<div style={{paddingBottom: "5px"}}>
					<FormControl fullWidth variant="outlined" size="large">
						{overridedManagers.length !== 0 && <InputLabel shrink={overridedManagers.length > 0}>
							Department Manager Overrides
						</InputLabel>}
						<Select
							placeholder={"Department Manager Overrides"}
							isMulti
							isClearable={false}
							value={overridedManagers.map((item) => ({
								label: item.user_name,
								value: item.user,
								pkey: item.Pkey,
							}))}
							onChange={async (newValues, actionMeta) => {
								if (actionMeta.action === "select-option") {
									const added = actionMeta.option;
									let userModel = new API.BuildClass(
										Types.Department_Overrides._name,
										API.RequestType.CREATE
									);

									userModel.object.user = added.value;
									userModel.object.role_name = type;

									await userModel.submit(null, "PUT");
								}

								if (actionMeta.action === "remove-value") {
									const removed = actionMeta.removedValue;

									let userModel = new API.BuildClass(
										Types.Department_Overrides._name,
										API.RequestType.DELETE
									);

									userModel.object.user = removed.value;
									userModel.object.pkey = removed.pkey;
									userModel.object.role_name = type;

									await userModel.submit(null, "DELETE");
								}

								await fetchManagers(true);
							}}
							options={availableManagers.map((item) => ({
								label: item.user_name,
								value: item.pkey,
							}))}
							styles={{
								control: (base, state) => ({
									...base,
									minHeight: 40,
									borderColor: state.isFocused ? "#1976d2" : "#ccc",
									boxShadow: state.isFocused ? "0 0 0 1px #1976d2" : "none",
									"&:hover": {
										borderColor: state.isFocused ? "#1976d2" : "#888"
									},
								}),
								menuPortal: (base) => ({...base, zIndex: 9999})
							}}
							menuPortalTarget={document.body}
						/>
					</FormControl>
				</div>
			)}
		</div>
	);
}